import type { ReactNode } from "react";
import { AlertTriangle, CheckCircle2, Info, Loader2, XCircle } from "lucide-react";
import { OP_SURFACE } from "@/lib/opSurfaces";
import { cn } from "@/lib/utils";

export type OpCalloutVariant = "help" | "process" | "success" | "warning" | "destructive";

const META: Record<
  OpCalloutVariant,
  { icon: typeof Info; label: string; box: string; iconClass: string }
> = {
  help: {
    icon: Info,
    label: "Pomoc",
    box: OP_SURFACE.help.box,
    iconClass: "text-yellow-700",
  },
  process: {
    icon: Loader2,
    label: "Proces",
    box: OP_SURFACE.process.box,
    iconClass: "animate-spin text-cyan-700",
  },
  // Zieleń = zielone światło operatora (wybrane/OK), nie tło strefy wyniku (indigo)
  success: {
    icon: CheckCircle2,
    label: "Wybrane",
    box: "rounded-md border-2 border-success border-l-8 border-l-success bg-success/10",
    iconClass: "text-success",
  },
  warning: {
    icon: AlertTriangle,
    label: "Uwaga",
    box: OP_SURFACE.review.box,
    iconClass: "text-orange-700",
  },
  destructive: {
    icon: XCircle,
    label: "Błąd",
    box: OP_SURFACE.danger.box,
    iconClass: "text-red-700",
  },
};

export function OpCallout({
  variant,
  title,
  children,
  className,
  showLabel = true,
}: {
  variant: OpCalloutVariant;
  title?: string;
  children?: ReactNode;
  className?: string;
  showLabel?: boolean;
}) {
  const meta = META[variant];
  const Icon = meta.icon;
  return (
    <div className={cn("border px-3 py-2.5 text-sm", meta.box, className)}>
      <div className="flex items-start gap-2">
        <Icon className={cn("mt-0.5 h-4 w-4 shrink-0", meta.iconClass)} aria-hidden />
        <div className="min-w-0 flex-1">
          {showLabel || title ? (
            <p className="font-semibold">
              {showLabel ? meta.label : null}
              {showLabel && title ? " · " : null}
              {title}
            </p>
          ) : null}
          {children ? <div className={cn((showLabel || title) && "mt-0.5")}>{children}</div> : null}
        </div>
      </div>
    </div>
  );
}
